import type { Entry } from "../types";

export const targetingMessages = {
  navChannels: { ko: "적용 대상 설정", en: "Target settings" },
  channelsIntro: {
    ko: "게시 채널은 자료가 어디에 게시되는지를, 자료 카테고리는 자료가 무엇에 관한 것인지를 나타냅니다. 여기에서 만든 항목은 검토 요청과 정책과 규칙 적용 범위 설정에 사용됩니다.",
    en: "Publishing channels describe where material appears; material categories describe what it is about. These targets are used by review requests and policy/rule scope settings.",
  },
  channels: { ko: "게시 채널 (어디에 게시하나요?)", en: "Publishing channels (where?)" },
  channelCode: { ko: "코드", en: "Code" },
  channelCodeHelp: {
    ko: "영문 대문자, 숫자, 밑줄만 사용합니다. 등록 후에는 변경할 수 없습니다.",
    en: "Uppercase letters, digits, and underscores only. It cannot be changed later.",
  },
  channelName: { ko: "게시 채널 이름", en: "Publishing channel name" },
  channelNamePlaceholder: { ko: "예: 상품안내장", en: "For example, Product brochure" },
  addChannel: { ko: "게시 채널 추가", en: "Add publishing channel" },
  sortOrder: { ko: "표시 순서", en: "Order" },
  usageCounts: { ko: "검토 {reviews}건, 규칙 {rules}건", en: "{reviews} reviews ,  {rules} rules" },
  inactiveLabel: { ko: "비활성", en: "Inactive" },
  deactivate: { ko: "비활성화", en: "Deactivate" },
  reactivate: { ko: "다시 활성화", en: "Reactivate" },
  showInactive: { ko: "비활성 포함", en: "Include inactive" },
  deletedBecameInactive: {
    ko: "기존 검토가 이 항목을 참조하고 있어 삭제하지 않고 비활성화했습니다. 기록은 그대로 보존됩니다.",
    en: "Existing reviews reference this item, so it was deactivated instead of deleted. The records are preserved.",
  },
  materialCategories: {
    ko: "자료 카테고리 (무엇에 관한 자료인가요?)",
    en: "Material categories (what?)",
  },
  categoryReviewCriteria: { ko: "필수 검토 항목", en: "Required review items" },
  categoryReviewCriteriaPlaceholder: {
    ko: "예) 변액보험\n- 원금 손실 가능성 안내\n- 예금자보호법 미적용 안내\n- 운용 실적에 따른 보험금 변동 안내",
    en: "e.g. Variable insurance\n- Possible loss of principal\n- Not covered by deposit insurance",
  },
  categoryReviewCriteriaHelp: {
    ko: "이 구분(및 하위 구분)의 자료를 검토할 때 AI가 항목마다 충족 여부를 확인하고, 누락되면 이 항목을 근거로 지적합니다. 저장 후 새로 제출되는 검토부터 적용됩니다.",
    en: "When reviewing material in this category (and its children), the AI checks each item and cites it when one is missing. Applies to reviews submitted after saving.",
  },
  materialCategoriesHelp: {
    ko: "상위 카테고리 아래에 하위 카테고리를 추가할 수 있습니다. 상위 카테고리를 선택하면 현재와 향후의 모든 하위 카테고리가 포함됩니다.",
    en: "Categories may contain child categories. Selecting a parent includes all current and future children.",
  },
  addCategory: { ko: "자료 카테고리 추가", en: "Add material category" },
  parentCategory: { ko: "상위 카테고리", en: "Parent category" },
  topLevelCategory: { ko: "최상위 카테고리", en: "Top-level category" },
  materialCategory: { ko: "자료 카테고리 (무엇에 관한 자료인가요?)", en: "Material category (what?)" },

  // Forbidden expression dictionary
  navDictionary: { ko: "금지 표현 사전", en: "Forbidden Expressions" },
  dictionaryIntro: {
    ko: "금지된 표현과 그 사유를 관리합니다. 등록된 표현이 광고 본문에서 발견되면 검토 결과에 근거와 함께 자동으로 지적됩니다.",
    en: "Manage banned expressions and why they are banned. A registered expression found in advertising copy is flagged automatically with its rationale.",
  },
  dictionaryEntryCount: { ko: "표현 {count}건", en: "{count} expressions" },
  expression: { ko: "표현, 단어", en: "Expression" },
  expressionPlaceholder: { ko: "예: 무조건 보장", en: "For example, guaranteed unconditionally" },
  dictionaryNote: { ko: "비고 (금지 사유)", en: "Note (why it is banned)" },
  dictionaryNotePlaceholder: {
    ko: "예: 지급 조건이 있어 단정적 표현을 사용할 수 없습니다.",
    en: "For example, payment conditions apply, so an absolute claim is not allowed.",
  },
  dictionarySuggestion: { ko: "대체 표현", en: "Suggested replacement" },
  dictionarySuggestionPlaceholder: { ko: "예: 약관에서 정한 조건 충족 시 보장", en: "For example, covered when policy conditions are met" },
  dictionaryActions: { ko: "관리", en: "Actions" },
  addExpression: { ko: "표현 추가", en: "Add expression" },
  editExpression: { ko: "금지 표현 수정", en: "Edit forbidden expression" },
  dictionarySearch: { ko: "표현, 사유 검색", en: "Search expression or note" },
  dictionaryEmpty: {
    ko: "등록된 금지 표현이 없습니다. 표현을 추가하거나 엑셀 내용을 붙여 넣어 일괄 등록하세요.",
    en: "No forbidden expressions yet. Add one, or paste your spreadsheet contents to import in bulk.",
  },
  dictionaryImport: { ko: "일괄 등록", en: "Bulk import" },
  dictionaryImportHelp: {
    ko: "한 줄에 하나씩 붙여 넣으세요. 형식: 구분,표현,비고,대체표현 (쉼표가 없으면 표현만 등록됩니다.)",
    en: "Paste one entry per line as: category,expression,note,suggestion. A line with no commas is treated as the expression only.",
  },
  dictionaryImportPlaceholder: {
    ko: "과장과 단정적 표현,무조건 보장,지급 조건이 있습니다,약관 조건 충족 시 보장",
    en: "EXAGGERATION,guaranteed unconditionally,payment conditions apply,covered when conditions are met",
  },
  dictionaryImportReplace: { ko: "이미 등록된 표현은 덮어쓰기", en: "Overwrite expressions that already exist" },
  dictionaryImportResult: {
    ko: "{created}건 등록, {updated}건 수정, {skipped}건 건너뜀",
    en: "{created} created, {updated} updated, {skipped} skipped",
  },
  dictionaryImportNothingParsed: {
    ko: "등록할 표현을 찾지 못했습니다. 형식을 확인해 주세요.",
    en: "No expressions could be read. Check the format.",
  },
  deleteExpressionConfirm: {
    ko: "‘{expression}’을(를) 삭제하시겠습니까?",
    en: "Delete “{expression}”?",
  },


  // Rule form: channel/category targeting and guide wording
  ruleChannels: { ko: "게시 채널 (어디에 게시하나요?)", en: "Publishing channels (where?)" },
  ruleChannelsAll: { ko: "모든 게시 채널", en: "All publishing channels" },
  ruleChannelsRequired: {
    ko: "게시 채널을 하나 이상 선택하거나 ‘모든 게시 채널’을 선택하세요.",
    en: "Select at least one publishing channel, or choose “All publishing channels”.",
  },
  ruleCategories: { ko: "자료 카테고리 (무엇에 관한 자료인가요?)", en: "Material categories (what?)" },
  ruleCategoriesAll: { ko: "모든 자료 카테고리", en: "All material categories" },
  guideSourceLabel: { ko: "가이드 출처 (구분)", en: "Guide source" },
  guideSourceUnset: { ko: "지정 안 함", en: "Not specified" },
  wordingTypeLabel: { ko: "고정문구 여부", en: "Wording type" },
  wordingTypeUnset: { ko: "해당 없음", en: "Not applicable" },
  requiredTextLabel: { ko: "안내 문구 (문구)", en: "Required wording" },
  requiredTextHelp: {
    ko: "고정문구로 지정하면 이 문구가 광고에 그대로 있는지 자동으로 확인합니다.",
    en: "When marked as fixed wording, the material is checked automatically for this exact text.",
  },
  requiredTextPlaceholder: {
    ko: "예: 보험계약 체결 전 상품설명서와 약관을 반드시 읽어보시기 바랍니다.",
    en: "For example, Please read the product manual and terms before purchasing.",
  },
  requiredTextRequired: {
    ko: "고정문구를 선택하면 실제 문구를 입력해야 합니다.",
    en: "Fixed wording requires the exact text.",
  },
  noChannelsConfigured: {
    ko: "등록된 채널이 없습니다. 채널과 자료 구분 화면에서 먼저 채널을 추가하세요.",
    en: "No channels exist yet. Add one on the Channels & Categories page first.",
  },

  // Policy list controls and lifecycle
  policySearch: { ko: "정책명, 발행 기관 검색", en: "Search title or authority" },
  policyStatusFilter: { ko: "상태", en: "Status" },
  policySourceFilter: { ko: "출처 분류", en: "Source" },
  policySort: { ko: "정렬", en: "Sort" },
  policySortNewest: { ko: "최신 등록순", en: "Newest first" },
  policySortOldest: { ko: "오래된 등록순", en: "Oldest first" },
  policySortTitle: { ko: "제목순", en: "Title" },
  policySortSource: { ko: "출처 분류순", en: "Source" },
  policyNoMatches: {
    ko: "조건에 맞는 정책이 없습니다. 검색어나 필터를 조정해 보세요.",
    en: "No policies match. Try adjusting your search or filters.",
  },
  archive: { ko: "보관", en: "Archive" },
  archived: { ko: "보관됨", en: "Archived" },
  deleted: { ko: "삭제됨", en: "Deleted" },
  archivePolicyConfirm: {
    ko: "‘{title}’ 정책을 보관하시겠습니까? 이후 검토에는 적용되지 않지만 모든 버전과 기록은 보존됩니다.",
    en: "Archive the policy “{title}”? It will no longer apply to new reviews, but every version and record is preserved.",
  },
  deletePolicyConfirm: {
    ko: "‘{title}’ 정책을 삭제하시겠습니까? 과거 검토 근거를 보존하기 위해 기록은 남지만, 정책은 더 이상 사용할 수 없습니다.",
    en: "Delete the policy “{title}”? Records are kept so historical review evidence stays intact, but the policy can no longer be used.",
  },
  archivedPolicyNotice: {
    ko: "보관된 정책입니다. 새 검토에는 적용되지 않으며 버전을 추가하거나 활성화할 수 없습니다. 보관을 해제하면 다시 편집할 수 있습니다.",
    en: "This policy is archived. It no longer applies to new reviews, and versions cannot be added or activated. Unarchive it to make it editable again.",
  },
  unarchive: { ko: "보관 해제", en: "Unarchive" },
  versionActivateWhenReady: {
    ko: "등록 후 이 버전을 자동으로 적용",
    en: "Apply this version automatically once registered",
  },
  versionActivateWhenReadyHint: {
    ko: "이전 버전을 대체해 검토에 적용됩니다. 문서 색인이 끝나면 자동으로 전환됩니다.",
    en: "It replaces the previous version in reviews, switching over as soon as the document finishes indexing.",
  },
  versionPendingAutoActivation: {
    ko: "색인이 끝나면 자동으로 적용됩니다.",
    en: "This version will be applied automatically once indexing finishes.",
  },
  unarchivePolicyConfirm: {
    ko: "‘{title}’ 정책의 보관을 해제하시겠습니까? 보관 당시 적용 중이던 버전이 다시 적용됩니다.",
    en: "Unarchive the policy “{title}”? The version that was in force when it was archived becomes active again.",
  },
  errUnarchive: { ko: "보관 해제에 실패했습니다.", en: "Unarchiving failed." },
  deletedPolicyNotice: {
    ko: "삭제된 정책입니다. 과거 검토 근거 확인용으로만 남아 있습니다.",
    en: "This policy is deleted. It remains only as evidence for past reviews.",
  },
  policyViewHistory: { ko: "버전 기록 보기 ({count})", en: "View version history ({count})" },
  policyHideHistory: { ko: "버전 기록 숨기기", en: "Hide version history" },
  policyLatestVersion: { ko: "최신 버전", en: "Latest version" },
  errArchive: { ko: "보관에 실패했습니다.", en: "Archiving failed." },
  errRetire: { ko: "비활성화에 실패했습니다.", en: "Retirement failed." },
  errRename: { ko: "이름 변경에 실패했습니다.", en: "Rename failed." },
  renamePolicy: { ko: "이름 변경", en: "Rename" },
  renamePolicyLabel: { ko: "새 정책 이름", en: "New policy name" },
  renamePolicySave: { ko: "저장", en: "Save" },
  renamePolicyCancel: { ko: "취소", en: "Cancel" },

  // Bulk policy upload: drop many documents, confirm the suggested details, register in one pass
  bulkUploadPolicies: { ko: "여러 정책 한 번에 등록", en: "Bulk upload policies" },
  bulkUploadDescription: {
    ko: "정책 문서를 한꺼번에 올리면 파일명에서 정책명, 출처 분류, 발행 기관, 시행일을 추정해 채워 둡니다. 등록 전에 값을 확인하고 수정하세요.",
    en: "Drop several policy documents at once and each file name is used to pre-fill the title, source, authority, and effective date. Check and correct the values before registering.",
  },
  bulkUploadDropzone: {
    ko: "파일을 여기로 끌어다 놓거나 클릭해 여러 개 선택",
    en: "Drop files here, or click to select several",
  },
  bulkUploadDropzoneHelp: {
    ko: "{extensions}, 파일당 최대 {size}",
    en: "{extensions} ,  up to {size} per file",
  },
  bulkUploadEmpty: {
    ko: "아직 선택한 파일이 없습니다.",
    en: "No files selected yet.",
  },
  bulkUploadReviewHint: {
    ko: "파일명에서 추정한 값입니다. 정확한지 확인한 뒤 등록하세요.",
    en: "These values were guessed from the file names. Confirm them before registering.",
  },
  bulkUploadStart: { ko: "{count}건 등록", en: "Register {count}" },
  bulkUploadRetry: { ko: "남은 {count}건 다시 시도", en: "Retry remaining {count}" },
  bulkUploadUploading: { ko: "등록 중...", en: "Registering..." },
  bulkUploadDoneClose: { ko: "완료", en: "Done" },
  bulkUploadProgress: { ko: "{done}/{total} 등록 완료", en: "{done} of {total} registered" },
  bulkUploadFailedCount: { ko: "{count}건 실패", en: "{count} failed" },
  bulkUploadStatusReady: { ko: "대기", en: "Ready" },
  bulkUploadStatusUploading: { ko: "등록 중", en: "Uploading" },
  bulkUploadStatusDone: { ko: "등록됨", en: "Registered" },
  bulkUploadStatusError: { ko: "실패", en: "Failed" },
  bulkUploadRejected: {
    ko: "다음 파일은 목록에 추가하지 않았습니다.",
    en: "These files were not added to the list.",
  },
  bulkUploadScope: { ko: "이 묶음의 적용 범위", en: "Scope for this batch" },
  bulkUploadScopeHelp: {
    ko: "선택한 범위가 이 묶음의 모든 문서에 적용됩니다. 등록 후 Sage Workflow에서 문서별로 조정할 수 있습니다.",
    en: "The selected scope applies to every document in this batch. You can adjust individual documents afterwards in Sage Workflow.",
  },
  bulkUploadScopeRequired: {
    ko: "적용 범위를 먼저 선택해야 등록할 수 있습니다.",
    en: "Choose the scope before you can register these documents.",
  },
  bulkUploadScopeAllWarning: {
    ko: "모든 채널과 모든 카테고리를 선택하면 이 문서들이 모든 심사에 근거 자료로 첨부됩니다. 심사 1건에 걸리는 시간이 길어지므로 꼭 필요한 경우에만 사용하세요.",
    en: "With all channels and all categories, these documents are attached to every review as a source. That makes each review slower, so use it only when it is genuinely required.",
  },
  bulkUploadDuplicateFile: {
    ko: "이미 목록에 있는 파일입니다.",
    en: "Already in the list.",
  },
  bulkUploadDuplicateTitle: {
    ko: "같은 이름의 정책이 이미 있습니다. 개정 문서라면 등록 대신 해당 정책에 새 버전으로 추가하세요.",
    en: "A policy with this title already exists. If this is a revision, add it as a new version of that policy instead.",
  },
  bulkUploadTitleRequired: {
    ko: "정책명이 비어 있는 항목 {count}건은 등록되지 않습니다. 이름을 입력하거나 항목을 제거하세요.",
    en: "{count} rows have no title and will be skipped. Enter a title or remove the row.",
  },
  bulkUploadCreated: { ko: "정책 {count}건을 등록했습니다.", en: "Registered {count} policies." },
  bulkUploadDiscardConfirm: {
    ko: "등록하지 않은 항목 {count}건이 있습니다. 닫으면 입력한 내용이 사라집니다. 닫으시겠습니까?",
    en: "{count} rows are not registered yet. Closing discards what you entered. Close anyway?",
  },

  // Compliance scope alerts for policies that no longer apply
  scopeInactivePolicyAlert: {
    ko: "적용 대상에 연결된 정책 {count}건이 보관 또는 삭제되어 더 이상 검토에 적용되지 않습니다. 연결된 다른 정책과 규칙은 정상적으로 적용됩니다.",
    en: "{count} policies mapped in this scope are archived or deleted and no longer apply to reviews. Other connected policies and rules still apply as usual.",
  },

  // System guide: the "how this system works" diagram, glossary, and menu tour
} as const satisfies Record<string, Entry>;
